import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE, whatsappLink } from "@/config/site";

const links = [
  { href: "#servicos", label: "Serviços" },
  { href: "#cambridge", label: "Cambridge" },
  { href: "#formacoes", label: "Formações" },
  { href: "#wellbeing", label: "Wellbeing" },
  { href: "#faq", label: "FAQ" },
  { href: "#contacto", label: "Contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-stone-200/70 shadow-[0_4px_24px_rgba(0,0,0,0.04)]" : "bg-transparent"
      }`}
    >
      <div className="container-tight flex items-center justify-between py-4">
        <a href="#top" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <div className="h-11 w-11 rounded-full bg-bordeaux-700 flex items-center justify-center overflow-hidden ring-1 ring-bordeaux-700/20">
            <img src={SITE.brand.logo} alt="iLg logo" className="h-9 w-9 object-contain invert-0" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-lg text-stone-900">Instituto Leopoldo Guimarães</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-bordeaux-700 font-semibold">Instituto de Formação · Academia de Estudos</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-4 py-2 text-sm font-medium text-stone-700 hover:text-bordeaux-700 transition-colors relative group"
            >
              {l.label}
              <span className="absolute inset-x-4 -bottom-0.5 h-px bg-bordeaux-700 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-btn"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bordeaux-700 text-white text-sm font-semibold hover:bg-bordeaux-800 transition-all hover:-translate-y-0.5 shadow-[0_6px_24px_rgba(124,31,44,0.18)]"
          >
            WhatsApp
          </a>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-stone-700 hover:border-bordeaux-700 hover:text-bordeaux-700 transition-colors"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white border-t border-stone-200 overflow-hidden"
          >
            <div className="container-tight flex flex-col gap-1 py-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="py-3 px-2 text-stone-800 hover:text-bordeaux-700 border-b border-stone-100 last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-whatsapp-btn"
                className="mt-3 text-center py-3 rounded-full bg-bordeaux-700 text-white font-semibold"
              >
                Falar no WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
